"use strict";

import { useRouter } from "next/router";
import {
  getRegionalizedTitleTag,
  i18nLocaleToIso3166,
  useRegionalize,
} from "@lululemon/ecom-common/src/util/locale-utils";
import { log } from "@lululemon/mwa-utils";

export const useRegionalizeSeoProps = () => {
  const { locale: nextLocale, locales, pathname } = useRouter();
  const { regionalizeLink } = useRegionalize();

  /**
   * @param {object} seoProps
   * @returns
   */
  const regionalizeSeoProps = (seoProps) => {
    try {
      // Deep copy the seoProps object to avoid mutating the original
      // Do not clone children, too big
      const seoLink =
        Array.isArray(seoProps?.link) && seoProps?.link?.length > 0
          ? seoProps.link
          : [];
      const mutatedSeoProps = {
        ...seoProps,
        link: JSON.parse(JSON.stringify(seoLink)),
        meta: JSON.parse(JSON.stringify(seoProps.meta)),
      };
      const canonicalLink = mutatedSeoProps.link?.find(
        (v) => v.rel === "canonical"
      )?.href;

      mutatedSeoProps?.link?.forEach((link) => {
        // Regionalize any links that contain an hrefLang
        if (link.hreflang)
          link.href = regionalizeLink(link.href, link.hreflang.toLowerCase());

        // We check for capital L'' hrefLang because that's how the SEO props are returned sometimes
        if (link.hrefLang)
          link.href = regionalizeLink(link.href, link.hrefLang.toLowerCase());

        // Regionalize the canonical tag
        if (link.rel === "canonical")
          link.href = regionalizeLink(link.href, nextLocale);
      });

      // Regionalize the title tag
      mutatedSeoProps.title = getRegionalizedTitleTag(
        nextLocale,
        mutatedSeoProps.title
      );

      // If no alternates are present, page is not CDP or PDP, and canonicalLink is truthy, generate hrefLangs
      if (
        !mutatedSeoProps?.link?.some((link) => link.rel === "alternate") &&
        !["/c/", "/p/"].some((path) => pathname.includes(path)) &&
        canonicalLink
      ) {
        // Loop through all i18n locales in Next's router
        locales.forEach((locale) => {
          mutatedSeoProps.link.push({
            rel: "alternate",
            hreflang: i18nLocaleToIso3166(locale),
            href: regionalizeLink(canonicalLink, locale),
          });
        });

        // Push the default alternate link
        mutatedSeoProps.link.push({
          rel: "alternate",
          hreflang: "x-default",
          href: regionalizeLink(canonicalLink, "en-us"),
        });
      }

      return mutatedSeoProps;
    } catch (error) {
      // If an error occurs, log the error and return the original SEO Props.
      // Errors related to SEO should be logged as warnings and captured via external monitoring tools (e.g. cloudwatch, sentry, and splunk)
      const renderType =
        typeof window !== "undefined" ? "client-side" : "server-side";
      log.warning("regionalizeSeoProps() encountered an error", error.message, {
        renderType,
        pathToRegionalize: pathname,
        nextLocale,
        error,
        stack: error.stack,
      });
    }

    return seoProps;
  };

  return { regionalizeSeoProps };
};
